import React from 'react'

import CartSummary from './components/CartSummary'
import Cookies from 'js-cookie';
import SpinnerOverlay from '../common/components/SpinnerOverlay';

import { axiosGet } from '../common/js/axios-utility'
import { updateCurrency, isValidCartId } from '../common/js/cart-utility';
import { type WebCart } from '../common/types/webshop.types'
import LoginModal, { type LoginModalHandle } from '../common/components/login-modal/LoginModal';
import { fetchLogin } from '../common/js/bfx-customer-utility';

import { useTranslation } from 'react-i18next'
import { ToastContainer } from 'react-toastify';

export default function app() {
    const [cartIsHidden, setCartIsHidden] = React.useState(true);
    const [loading, setLoading] = React.useState(true);
    const [cart, setCart] = React.useState<WebCart | null>(null);

    const loginModalRef = React.useRef<LoginModalHandle>(null);
    const invalidCurrencyModalRef = React.useRef<HTMLDialogElement>(null);

    const handleShowCart = React.useRef<(event: CustomEvent) => void>(null);
    const handleHideCart = React.useRef<(event: CustomEvent) => void>(null);
    const handleToggleCart = React.useRef<(event: CustomEvent) => void>(null);

    const { t } = useTranslation(['checkout', 'common']);

    let $click: boolean | null;

    const cartId = React.useMemo(() =>{
        return Cookies.get('bfx-checkout-id');
    }, [cartIsHidden])

    React.useEffect(() => {
        const doFetch = async () => {
            setLoading(true);
            await fetchCart();
            setLoading(false);
        }
        
        //These functions need to live within the useEffect,
        //otherwise the cartIsHidden var will always be set
        //to its default when these handlers are called.
        handleShowCart.current = async () => {
            if (!cartIsHidden){
                doFetch();
            } else {
                setCartIsHidden(false);
            }
        }
        handleHideCart.current = () => {
            setCartIsHidden(true);
        }
        handleToggleCart.current = () => {
            $click = true;
            setCartIsHidden(!cartIsHidden);
        }
        //Remove any existing listeners
        window.removeEventListener('show-cart', handleShowCart.current);
        window.removeEventListener('hide-cart', handleHideCart.current);
        window.removeEventListener('toggle-cart', handleToggleCart.current);

        window.addEventListener('show-cart', handleShowCart.current);
        window.addEventListener('hide-cart', handleHideCart.current);
        window.addEventListener('toggle-cart', handleToggleCart.current);

        doFetch();
    }, [cartIsHidden])


    React.useEffect(() => {
        const handleDocumentClick = (event: MouseEvent) => {
            const clickedElement = event.target;
            if (clickedElement instanceof Element){
                if (!document.getElementById('cart-app')!.contains(clickedElement) && !$click && !clickedElement.classList.contains('not-close')) {
                    setCartIsHidden(true);
                } else if($click) {
                    $click = false;
                }
            }
        };

        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [setCartIsHidden]);

    const fetchCart = async () => {
        const cartId = Cookies.get('bfx-checkout-id');
        if (!isValidCartId(cartId)){
            resetCart();
            return;
        }

        try{
            const {data: result} = await axiosGet(`/shop/cart/${cartId}`);
            if (result.data && result.data.status !== 'complete') {
                setCart(result.data);
            } else if (result.data.status === 'complete') {
                resetCart();
            } else{
               resetCart();
               closeCart(); 
            }

            let cartIcon = document.getElementById("cartIcon");
            if(result.data && result.data.lineItems) {
                if(cartIcon) {
                    cartIcon.setAttribute("data-products-count", result.data.lineItems.length);
                }
            } else {
                if(cartIcon) {
                    cartIcon.setAttribute("data-products-count", '0');
                }
            }
        } catch(err) {
            let cartIcon = document.getElementById("cartIcon");
            if(cartIcon) {
                cartIcon.setAttribute("data-products-count", '0');
            }
            resetCart();
        }
    }

    const resetCart = () => {
        Cookies.remove('bfx-checkout-id');
        setCart(null);
    }

    const closeCart = () => {
        const hideEvent = new Event('hide-cart');
        window.dispatchEvent(hideEvent);
    }

    const handleCartUpdate = async () => {
        await fetchCart();
    }

    const handleCurrencyChange = async (newIso: string) => {
        setLoading(true);
        const login = fetchLogin();
        if (isValidCartId(cartId)){
            try{
                await updateCurrency(newIso, 'web');
            }
            catch(err: any){
                if (err.message == 'invalid currency'){
                    handleInvalidCurrency();
                    setLoading(false);
                    return;
                }
            }
        }
        await fetchCart();
        setLoading(false);
    }

    const handleLogin = async () => {
        _renderLoginBar();
        loginModalRef.current!.hide();
    }

    const handleCheckout = () => {
        window.location.href = '/store/checkout';
    }

    const handleInvalidCurrency = () => {
        invalidCurrencyModalRef.current!.showModal();
    }

    const cartClass = cartIsHidden ? 'd-none' : '';

    return (
        <>
        <ToastContainer containerId="cart" />
        <div id="cart-app" className={cartClass}>
            <div className="cart">
                <SpinnerOverlay hidden={!loading} size={75}>
                    <CartSummary
                        cart={cart}
                        header={t('cart.header') as string}
                        showCheckoutButton={cart != null && cart.lineItems.length > 0}
                        showCloseButton
                        onClose={closeCart}
                        onUpdate={handleCartUpdate}
                        setLoading={setLoading}
                        onCurrencyChange={handleCurrencyChange}
                        onCheckout={handleCheckout}
                    />
                </SpinnerOverlay>
            </div>
        </div>
        <div>
            <LoginModal ref={loginModalRef} 
                        message='Please sign in to access additional currency options.' 
                        onComplete={handleLogin} />
        </div>
        </>
    )
}
